import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { RefreshProvider } from "./contexts/RefreshContext";
import { Toaster } from "./components/ui/sonner";
import { roleHome } from "./lib/rbac";
import { effective, getMyAccess, matchItem } from "./lib/menuRegistry";
import Login from "./pages/Login";
import AuthComplete from "./pages/AuthComplete";
// New (MS SQL-backed) primary screens
import MsDashboard from "./pages/MsDashboard";
import MsPOs from "./pages/MsPOs";
import MsPODetail from "./pages/MsPODetail";
import MsDispatch from "./pages/MsDispatch";
import MsDispatchDetail from "./pages/MsDispatchDetail";
import Shipping from "./pages/Shipping";
import OtherCharges from "./pages/OtherCharges";
import ShipmentDetail from "./pages/ShipmentDetail";
import MsOpenOrders from "./pages/MsOpenOrders";
import MsOtdRisk from "./pages/MsOtdRisk";
import Reports from "./pages/Reports";
import ShortClosed from "./pages/ShortClosed";
import DispatchPlan from "./pages/DispatchPlan";
import DispatchPlanMock from "./pages/DispatchPlanMock";
import DispatchPlans from "./pages/DispatchPlans";
import CfsCutoff from "./pages/CfsCutoff";
import TransferPricing from "./pages/TransferPricing";
import ImportExcel from "./pages/ImportExcel";
// Legacy / Adhoc Entry (kept available on the side as requested)
import Inbox from "./pages/Inbox";
import PortalPush from "./pages/PortalPush";
import Failures from "./pages/Failures";
import PODetail from "./pages/PODetail";
import Orders from "./pages/Orders";
import IntakeReview from "./pages/IntakeReview";
import ManualPO from "./pages/ManualPO";
import IntakeAudit from "./pages/IntakeAudit";
import RmpLookup from "./pages/RmpLookup";
import Customers from "./pages/Customers";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import Cockpit from "./pages/Cockpit";
import DataHealth from "./pages/DataHealth";
import POReviews from "./pages/POReviews";
import AdminX from "./pages/AdminX";
import DispatchRegister from "./pages/DispatchRegister";
import CustomerStatusReport from "./pages/CustomerStatusReport";
import Logistics from "./pages/Logistics";
import ContainerLoading from "./pages/ContainerLoading";
import QuickBooks from "./pages/QuickBooks";
import SapCredentials from "./pages/SapCredentials";
import SapHealth from "./pages/SapHealth";
import Reconcile from "./pages/Reconcile";
import Explorer from "./pages/Explorer";
import SalesMonthly from "./pages/SalesMonthly";
import SalesReport from "./pages/SalesReport";
import Forecasts from "./pages/Forecasts";
import OtherForecastNew from "./pages/OtherForecastNew";
import OtherForecastView from "./pages/OtherForecastView";
import ShipWindow from "./pages/ShipWindow";
import POTraining from "./pages/POTraining";
import SapPush from "./pages/SapPush";
import SapPushSoap from "./pages/SapPushSoap";
import SapOpenOrders from "./pages/SapOpenOrders";
import SapMapping from "./pages/SapMapping";
import CustomerAddressExplorer from "./pages/CustomerAddressExplorer";
import SapOutbound from "./pages/SapOutbound";
import SapDispatchTest from "./pages/SapDispatchTest";
import DispatchSO from "./pages/DispatchSO";
import DispatchCombine from "./pages/DispatchCombine";
import SapInvoicing from "./pages/SapInvoicing";
import ShippingBills from "./pages/ShippingBills";
import ShippingBillAnalytics from "./pages/ShippingBillAnalytics";
import FALogistics from "./pages/FALogistics";
import BulkReceiptUpload from "./pages/BulkReceiptUpload";
import ReceiptsFromAdvice from "./pages/ReceiptsFromAdvice";
import SapPostAudit from "./pages/SapPostAudit";
import AddressMap from "./pages/AddressMap";
import AccessControl from "./pages/AccessControl";
import "./App.css";

// Gate protected pages: don't render (or fire authenticated API calls) until we
// know the user is authenticated. Redirect to /login when there's no session.
function RequireAuth({ children }) {
  const { user, loading, menuAccessLoaded } = useAuth();
  const location = useLocation();
  const token = localStorage.getItem("jde_token");
  if (loading) {
    return (
      <div data-testid="auth-loading" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 14 }}>
        Loading…
      </div>
    );
  }
  if (!token && !user) return <Navigate to="/login" replace />;
  // Trainers are locked to the single Training Studio page.
  if (user?.role === "trainer") return <Navigate to="/train" replace />;
  // DB-driven menu/route access (managed on the Access Control page). super_admin + admin are full.
  // Falls back to legacy defaults until a role's access is configured. Backend API gates still apply.
  if (user && user.role !== "super_admin") {
    // Wait for the effective access to load so direct URL loads / refreshes honour the DB config.
    if (!menuAccessLoaded) {
      return (
        <div data-testid="auth-loading" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 14 }}>
          Loading…
        </div>
      );
    }
    const eff = effective(user.role, getMyAccess());
    if (!eff.full) {
      const m = matchItem(location.pathname);
      if (m && !(eff.groups.has(m.code) && eff.items.has(m.to))) {
        return <Navigate to={roleHome(user.role)} replace />;
      }
    }
  }
  return children;
}

// Training Studio: admins and dedicated trainers only.
function RequireTrainer({ children }) {
  const { user, loading } = useAuth();
  const token = localStorage.getItem("jde_token");
  if (loading) {
    return (
      <div data-testid="auth-loading" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 14 }}>
        Loading…
      </div>
    );
  }
  if (!token && !user) return <Navigate to="/login" replace />;
  if (user && !["admin", "super_admin", "trainer", "manager"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

// Setup features after Excel Import (Portal Push, ERP Reconcile, Adhoc Entry, Settings)
// are restricted to the Super Admin. Everyone else is redirected to the dashboard.
function RequireSuperAdmin({ children }) {
  const { user, loading } = useAuth();
  const token = localStorage.getItem("jde_token");
  if (loading) {
    return (
      <div data-testid="auth-loading" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 14 }}>
        Loading…
      </div>
    );
  }
  if (!token && !user) return <Navigate to="/login" replace />;
  if (user && user.role !== "super_admin") return <Navigate to="/" replace />;
  return children;
}

// Access Control page — admins + super admins only.
function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const token = localStorage.getItem("jde_token");
  if (loading) {
    return (
      <div data-testid="auth-loading" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 14 }}>
        Loading…
      </div>
    );
  }
  if (!token && !user) return <Navigate to="/login" replace />;
  if (user && user.role !== "super_admin" && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <RefreshProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/complete" element={<AuthComplete />} />
          {/* Primary MS-SQL flows */}
          <Route path="/" element={<RequireAuth><MsDashboard /></RequireAuth>} />
          <Route path="/pos" element={<RequireAuth><MsPOs /></RequireAuth>} />
          <Route path="/pos/:pono" element={<RequireAuth><MsPODetail /></RequireAuth>} />
          <Route path="/dispatch" element={<RequireAuth><MsDispatch /></RequireAuth>} />
          <Route path="/dispatch/:saleNo" element={<RequireAuth><MsDispatchDetail /></RequireAuth>} />
          <Route path="/shipping" element={<RequireAuth><Shipping /></RequireAuth>} />
          <Route path="/shipping/inv/:invNo" element={<RequireAuth><ShipmentDetail /></RequireAuth>} />
          <Route path="/shipping/:containerNo" element={<RequireAuth><ShipmentDetail /></RequireAuth>} />
          <Route path="/other-charges" element={<RequireAuth><OtherCharges /></RequireAuth>} />
          <Route path="/dispatch-plan" element={<RequireAuth><DispatchPlan /></RequireAuth>} />
          <Route path="/dispatch-plans" element={<RequireAuth><DispatchPlans /></RequireAuth>} />
          <Route path="/cfs-cutoff" element={<RequireAuth><CfsCutoff /></RequireAuth>} />
          <Route path="/transfer-pricing" element={<RequireAuth><TransferPricing /></RequireAuth>} />
          <Route path="/dispatch-plan-design" element={<RequireAuth><DispatchPlanMock /></RequireAuth>} />
          <Route path="/open-orders" element={<RequireAuth><MsOpenOrders /></RequireAuth>} />
          <Route path="/explorer" element={<RequireAuth><Explorer /></RequireAuth>} />
          <Route path="/sales" element={<RequireAuth><SalesMonthly /></RequireAuth>} />
          <Route path="/sales-report" element={<RequireAuth><SalesReport /></RequireAuth>} />
          <Route path="/forecasts" element={<Navigate to="/forecasts/walmart/overview" replace />} />
          <Route path="/forecasts/other/new" element={<RequireAuth><OtherForecastNew /></RequireAuth>} />
          <Route path="/forecasts/other/view" element={<RequireAuth><OtherForecastView /></RequireAuth>} />
          <Route path="/forecasts/:customer/:view" element={<RequireAuth><Forecasts /></RequireAuth>} />
          <Route path="/ship-window" element={<RequireAuth><ShipWindow /></RequireAuth>} />
          <Route path="/otd-risk" element={<RequireAuth><MsOtdRisk /></RequireAuth>} />
          <Route path="/reports" element={<RequireAuth><Reports /></RequireAuth>} />
          <Route path="/short-closed" element={<RequireAuth><ShortClosed /></RequireAuth>} />
          <Route path="/po-reviews" element={<RequireAuth><POReviews /></RequireAuth>} />
          <Route path="/import" element={<RequireAuth><ImportExcel /></RequireAuth>} />
          {/* Customers stays */}
          <Route path="/customers" element={<RequireAuth><Customers /></RequireAuth>} />
          {/* Adhoc Entry — legacy email/upload flow (Super Admin only) */}
          <Route path="/inbox" element={<RequireSuperAdmin><Inbox /></RequireSuperAdmin>} />
          <Route path="/portal-push" element={<RequireSuperAdmin><PortalPush /></RequireSuperAdmin>} />
          <Route path="/reconcile" element={<RequireSuperAdmin><Reconcile /></RequireSuperAdmin>} />
          <Route path="/failures" element={<RequireAuth><Failures /></RequireAuth>} />
          <Route path="/po/new" element={<RequireAuth><ManualPO /></RequireAuth>} />
          <Route path="/po/:id" element={<RequireAuth><PODetail /></RequireAuth>} />
          <Route path="/orders" element={<RequireAuth><Orders /></RequireAuth>} />
          <Route path="/quickbooks" element={<RequireAdmin><QuickBooks /></RequireAdmin>} />
          <Route path="/intake-review" element={<RequireAuth><IntakeReview /></RequireAuth>} />
          <Route path="/intake-audit" element={<RequireAuth><IntakeAudit /></RequireAuth>} />
          {/* Admin */}
          <Route path="/users" element={<RequireAuth><Users /></RequireAuth>} />
          <Route path="/access-control" element={<RequireAdmin><AccessControl /></RequireAdmin>} />
          <Route path="/rmp-lookup" element={<RequireAuth><RmpLookup /></RequireAuth>} />
          <Route path="/settings" element={<RequireSuperAdmin><Settings /></RequireSuperAdmin>} />
          <Route path="/cockpit" element={<RequireAuth><Cockpit /></RequireAuth>} />
          <Route path="/data-health" element={<RequireAuth><DataHealth /></RequireAuth>} />
          <Route path="/adminx" element={<RequireAuth><AdminX /></RequireAuth>} />
          <Route path="/dispatch-register" element={<RequireAuth><DispatchRegister /></RequireAuth>} />
          <Route path="/customer-status-report" element={<RequireAuth><CustomerStatusReport /></RequireAuth>} />
          <Route path="/logistics" element={<RequireAuth><Logistics /></RequireAuth>} />
          <Route path="/logistics/analysis" element={<RequireAuth><Logistics /></RequireAuth>} />
          <Route path="/sap-credentials" element={<RequireAuth><SapCredentials /></RequireAuth>} />
          <Route path="/sap-health" element={<RequireAuth><SapHealth /></RequireAuth>} />
          <Route path="/logistics/shipping-bills" element={<RequireAuth><ShippingBills /></RequireAuth>} />
          <Route path="/logistics/shipping-bills/analytics" element={<RequireAuth><ShippingBillAnalytics /></RequireAuth>} />
          <Route path="/logistics/container-loading" element={<RequireAuth><ContainerLoading /></RequireAuth>} />
          <Route path="/fa-logistics" element={<RequireAuth><FALogistics /></RequireAuth>} />
          <Route path="/fa-logistics/bulk-upload" element={<RequireAuth><BulkReceiptUpload /></RequireAuth>} />
          <Route path="/fa-logistics/receipts-from-advice" element={<RequireAuth><ReceiptsFromAdvice /></RequireAuth>} />
          <Route path="/fa-logistics/sap-post-audit" element={<RequireAuth><SapPostAudit /></RequireAuth>} />
          <Route path="/sap/address-map" element={<RequireAuth><AddressMap /></RequireAuth>} />
          <Route path="/sappush" element={<RequireAdmin><SapPush /></RequireAdmin>} />
          <Route path="/sappush-soap" element={<RequireAuth><SapPushSoap /></RequireAuth>} />
          <Route path="/sap/open-orders-report" element={<RequireAdmin><SapOpenOrders /></RequireAdmin>} />
          <Route path="/sap-mapping" element={<RequireAuth><SapMapping /></RequireAuth>} />
          <Route path="/sap/customer-addresses" element={<RequireAuth><CustomerAddressExplorer /></RequireAuth>} />
          <Route path="/sap/outbound" element={<RequireAuth><SapOutbound /></RequireAuth>} />
          <Route path="/sap/dispatch-test" element={<RequireAuth><DispatchCombine /></RequireAuth>} />
          <Route path="/sap/dispatch-so" element={<RequireAdmin><DispatchSO /></RequireAdmin>} />
          <Route path="/sap/dispatch-test-legacy" element={<RequireAdmin><SapDispatchTest /></RequireAdmin>} />
          <Route path="/sap/invoicing" element={<RequireAuth><SapInvoicing /></RequireAuth>} />
          {/* PO Training Studio — trainers (locked here) + admins */}
          <Route path="/train" element={<RequireTrainer><POTraining /></RequireTrainer>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
      </RefreshProvider>
    </AuthProvider>
  );
}

export default App;
